import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface ReportData {
  domain: string;
  keywordTitle?: string;
  generatedAt: string;
  scoreChange: number;
  reportTitle?: string;
  summaryMetrics?: { title: string; metrics: { label: string; value: string }[]; }
  metrics: { label: string; value: string; change?: string }[];
  keywords: { keyword: string; position?: number; yourPosition?: number; competitorPosition?: number; searchVolume?: number; volume?: number; difficulty?: number; trafficPotential?: number, cpc?: number; source?: String, competition?: number, opportunity?: string; }[];
  positionDistribution?: {
    year: number;
    month: number;
    keywordsCount: number;
    top1_5: number;
    top6_10: number;
    top11_20: number;
    top21_50: number;
    top51_100: number;
  }[];
  positionChanges?: Array<{ keyword: string; position: number; prevPosition: number; change: number; }>;
  contentRefreshOpportunities?: {
    keyword: string;
    url?: string;
    searchVolume: number;
    opportunity: string;
    reason: string
  }[];
  questionKeywords?: { keyword: string; volume?: number; difficulty?: number }[];
  findings: { title: string; severity: string; detail: string }[];
  technicalIssues: { issue: string; count: number; severity: string }[];
  trafficHistory?: {
    month: number;
    year: number;
    trafficSum: number;
    keywordsCount: number;
    top1_5: number;
    top6_10: number;
    top11_20: number;
  }[];
  worldwideOverview?: { source: string; traffic: number; keywords: number; price: number }[];
  referringDomains?: { domain: string; backlinks: number; domainAuthority: number }[];
  anchorText?: { anchor: string; backlinksCount: number; referringDomainsCount: number }[];
  serpFeatures?: { keyword: string; position?: number; volume?: number; traffic?: number; serpFeatures: String[] }[];
  competitors?: { domain: String; authorityScore: number; traffic: number; commonKeywords: number; overlap: number; share?: number }[];
  newLinks?: { urlFrom: string; urlTo: string; anchor: string; firstSeen: string }[];
  lostLinks?: { urlFrom: string; urlTo: string; anchor: string; firstSeen: string }[];
  topPages?: { url: string; keywordsCount: number; trafficSum: number }[];
  analysis?: string;
}

const COLORS = {
  purple: [91, 61, 245] as [number, number, number],
  darkPurple: [30, 15, 80] as [number, number, number],
  orange: [255, 122, 26] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  lightText: [228, 228, 255] as [number, number, number],
  muted: [180, 176, 216] as [number, number, number],
  green: [74, 222, 128] as [number, number, number],
  red: [248, 113, 113] as [number, number, number],
  bgDark: [14, 10, 36] as [number, number, number],
  bgCard: [25, 18, 60] as [number, number, number],
};

const getOpportunityColor = (opp: string): {
  bg: [number, number, number];
  text: [number, number, number];
  border: [number, number, number];
} => {
  const normalized = (opp || "").toUpperCase();
  if (normalized === "HIGH") return { bg: [20, 83, 45], text: [74, 222, 128], border: [34, 197, 94] };
  if (normalized === "LOW") return { bg: [51, 65, 85], text: [148, 163, 184], border: [100, 116, 139] };
  return { bg: [92, 45, 20], text: [255, 154, 74], border: [255, 154, 74] };
};


function severityColor(sev: string): [number, number, number] {
  switch (sev) {
    case "HIGH": return COLORS.red;
    case "MED": return COLORS.orange;
    case "LOW": return COLORS.green;
    default: return COLORS.muted;
  }
}

/**
 * Strip all non-ASCII characters (emoji, special symbols) that jsPDF can't render.
 * Also clean markdown formatting.
 */
function cleanText(text: string): string {
  return text
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/[^\x00-\x7F]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Paint the full page background dark. Called on every new page.
 */
function paintPageBg(doc: jsPDF) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(...COLORS.bgDark);
  doc.rect(0, 0, w, h, "F");
}

function createTable(doc, margin, options) {
  autoTable(doc, {
    theme: "plain",
    styles: {
      fillColor: COLORS.bgCard,
      textColor: COLORS.lightText,
      fontSize: 9,
      cellPadding: 3,
      lineWidth: 0,
      overflow: "linebreak"
    },

    headStyles: {
      fillColor: COLORS.darkPurple,
      textColor: COLORS.purple,
      fontStyle: "bold",
      fontSize: 8,
    },

    alternateRowStyles: {
      fillColor: [20, 14, 50],
    },

    rowPageBreak: 'avoid',      // prevent row split
    margin: { left: margin, right: margin },

    didDrawPage: () => {
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      if (currentPage > 1) {
        doc.setPage(currentPage);
      }
    },

    ...options, // allow overrides per table
  });
};

export function generateSEOReport(data?: ReportData): void {
  if (!data) {
    console.warn("No audit data available for PDF generation");
    return;
  }

  const doc = new jsPDF("p", "mm", "a4");

  // === GLOBAL BACKGROUND FOR ALL PAGES (new solution) ===
  doc.internal.events.subscribe('addPage', () => {
    paintPageBg(doc);
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;
  let pageNum = 1;

  const addFooter = () => {
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    doc.text(`OG01 Ruby AI Suite  |  ${data.generatedAt}  |  Page ${pageNum}`, margin, pageHeight - 10);
  };

  const newPage = () => {
    doc.addPage();
    pageNum++;
    y = 20;
  };

  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      newPage();
    }
  };

  const sectionTitle = (title: string) => {
    checkPageBreak(15);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.white);
    doc.text(title, margin, y);
    y += 2;
    doc.setDrawColor(...COLORS.purple);
    doc.setLineWidth(0.5);
    doc.setLineCap("round");
    const textWidth = doc.getTextWidth(title);  // Calculate text width
    doc.line(margin, y, margin + textWidth + 2, y);
    y += 8;
  };

  // ══════════════════════════════════════════════
  // PAGE 1: Cover
  // ══════════════════════════════════════════════

  // Background — dark navy
  doc.setFillColor(14, 10, 36);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // Header band — darker purple
  doc.setFillColor(40, 20, 100);
  doc.rect(0, 0, pageWidth, 68, "F");

  // Purple accent line
  doc.setFillColor(91, 61, 245);
  doc.rect(0, 68, pageWidth, 2, "F");

  // OG01 logo text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("OG01", margin, 18);

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(26);
  doc.setFont("helvetica", "bold");
  doc.text(data.reportTitle || "Search Intelligence Report", margin, 38);

  // Domain
  doc.setTextColor(200, 200, 240);
  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text(cleanText(data.domain), margin, 52);

  // Date
  doc.setTextColor(160, 155, 200);
  doc.setFontSize(9);
  doc.text("Generated: " + cleanText(data.generatedAt), margin, 62);

  // ── Domain Metrics Box ──
  if (data.metrics && data.metrics.length > 0) {
    y = 85;

    const primary = data.metrics[0];

    const boxHeight = 50;

    // Metrics box background
    doc.setFillColor(30, 22, 70);
    doc.roundedRect(margin, y, contentWidth, boxHeight, 4, 4, "F");

    doc.setDrawColor(91, 61, 245);
    doc.setLineWidth(0.8);
    doc.roundedRect(margin, y, contentWidth, boxHeight, 4, 4, "S");

    // Shared Y positions
    const valueY = y + 30;
    const labelY = valueY + 10;

    // Define left section width
    const leftSectionWidth = 45;

    // Perfect horizontal center of left block
    const centerX = margin + leftSectionWidth / 2;

    // === BIG NUMBER ===
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(40);
    doc.setFont("helvetica", "bold");
    doc.text(String(primary?.value), centerX, valueY, {
      align: "center"
    });

    // === LABEL ===
    doc.setTextColor(160, 155, 200);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(primary.label.toUpperCase(), centerX, labelY, {
      align: "center"
    });

    // === RIGHT SIDE METRICS ===
    const remainingMetrics = data.metrics.slice(1);
    const metricsStartX = margin + leftSectionWidth;
    const metricSpacing = (contentWidth - leftSectionWidth) / remainingMetrics.length;

    remainingMetrics.forEach((metric, i) => {
      const mx = metricsStartX + i * metricSpacing;

      // Label
      doc.setTextColor(160, 155, 200);
      doc.setFontSize(8);
      doc.text(metric.label.toUpperCase(), mx, y + 16);

      // Value
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(cleanText(String(metric.value)), mx, valueY);
    });

    y = 148;
  } else {
    y = 85;
  }

  // ── Technical Issues ──
  if (data.technicalIssues && data.technicalIssues.length > 0) {
    sectionTitle("Technical Issues");

    data.technicalIssues.forEach((issue) => {
      checkPageBreak(16);
      doc.setFillColor(...COLORS.bgCard);
      doc.roundedRect(margin, y, contentWidth, 12, 2, 2, "F");

      const sevColor = severityColor(issue.severity);
      doc.setFillColor(...sevColor);
      doc.circle(margin + 5, y + 6, 1.5, "F");

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.lightText);
      doc.text(cleanText(issue.issue), margin + 12, y + 7);

      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...sevColor);
      doc.text(issue.severity, margin + contentWidth - 15, y + 7);

      y += 15;
    });
    y += 5;
  }

  // ── Findings ──
  if (data.findings && data.findings.length > 0) {
    sectionTitle("Findings & Recommendations");

    data.findings.forEach((finding) => {
      checkPageBreak(16);
      doc.setFillColor(...COLORS.bgCard);
      doc.roundedRect(margin, y, contentWidth, 12, 2, 2, "F");

      const sevColor = severityColor(finding.severity);
      doc.setFillColor(...sevColor);
      doc.circle(margin + 5, y + 6, 1.5, "F");

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.lightText);
      doc.text(cleanText(finding.title).substring(0, 85), margin + 12, y + 7);

      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...sevColor);
      doc.text(finding.severity, margin + contentWidth - 15, y + 7);

      y += 15;
    });
    y += 5;
  }

  // ── Keywords Table ──
  if (data.keywords && data.keywords?.length > 0) {
    sectionTitle(data.keywordTitle || 'Keywords');

    const hasPosition = data.keywords.some(k => k.position !== undefined);
    const hasCompetitorPosition = data.keywords.some(k => k.competitorPosition !== undefined);
    const hasYourPosition = data.keywords.some(k => k.yourPosition !== undefined);
    const hasTraffic = data.keywords.some(k => k.trafficPotential !== undefined);
    const hasCpc = data.keywords.some(k => k.cpc !== undefined);
    const hasSource = data.keywords.some(k => k.source !== undefined);
    const hasCompetition = data.keywords.some(k => k.competition !== undefined);
    const hasOpportunity = data.keywords.some(k => k.opportunity !== undefined);


    const head = [["Keyword"]];

    if (hasPosition) head[0].push("Position");
    if (hasCompetitorPosition) head[0].push("Comp Pos.");
    if (hasYourPosition) head[0].push("Your Pos.");

    head[0].push("Volume");
    head[0].push("Difficulty");
    if (hasTraffic) head[0].push("Traffic Potential");

    if (hasCpc) head[0].push("CPC");
    if (hasSource) head[0].push("Source");
    if (hasCompetition) head[0].push("Competition");
    if (hasOpportunity) head[0].push("Opportunity");


    const body = data.keywords.map((kw) => {
      const row: any[] = [];

      row.push(cleanText(kw.keyword));

      if (hasPosition) row.push(kw.position ? `#${kw.position}` : "-");
      if (hasCompetitorPosition) row.push(kw.competitorPosition ? `#${kw.competitorPosition}` : "-");
      if (hasYourPosition) row.push(kw.yourPosition ? `#${kw.yourPosition}` : "Not ranking");
      row.push(Number(kw.volume ?? kw.searchVolume ?? 0).toLocaleString());
      row.push(kw.difficulty ?? 0);
      if (hasTraffic) row.push(kw.trafficPotential.toLocaleString() ?? "-");
      if (hasCpc) row.push(`$${Number(kw.cpc ?? 0).toFixed(2)}`);
      if (hasSource) row.push(cleanText(String(kw.source ?? "-")));
      if (hasCompetition) row.push(`$${Number(kw.competition ?? 0).toFixed(2)}`);
      if (hasOpportunity) row.push(cleanText(String(kw.opportunity ?? "-")));

      return row;
    });

    const trafficIndex = head[0].indexOf("Traffic Potential");
    const difficultyIndex = head[0].indexOf("Difficulty");
    const opportunityIndex = head[0].indexOf("Opportunity");

    createTable(doc, margin, {
      startY: y,
      head,
      body,
      columnStyles: {
        ...(trafficIndex !== -1 && {
          [trafficIndex]: { textColor: COLORS.orange, fontStyle: "bold" }
        }),
        [head[0].indexOf("Difficulty")]: { cellWidth: 28 }
      },
      didParseCell: (data: any) => {
        const compPositionIndex = head[0].indexOf("Comp Pos.");
        const yourPositionIndex = head[0].indexOf("Your Pos.");
        if (data.column.index === compPositionIndex && data.cell.section === "body") {
          const value = data.cell.raw;

          if (value && value !== "-") {
            data.cell.styles.textColor = COLORS.green;
            data.cell.styles.fontStyle = "bold";
          }
        }

        if (data.column.index === yourPositionIndex && data.cell.section === "body") {
          const value = data.cell.raw;

          if (value && value !== "Not ranking") {
            data.cell.styles.textColor = COLORS.red;
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = COLORS.muted;
          }

        }

        if ((data.column.index === opportunityIndex || data.column.index === difficultyIndex) && data.cell.section === "body") {
          data.cell.text = [''];
        }
      },
      didDrawCell: (data: any) => {
        if (data.column.index === difficultyIndex && data.cell.section === "body") {
          const value = Number(data.cell.raw || 0);

          const padding = 2;
          const barHeight = 3;

          const barX = data.cell.x + padding;
          const barY = data.cell.y + (data.cell.height / 2) - (barHeight / 2);

          const maxBarWidth = data.cell.width - 14;
          const barWidth = (value / 100) * maxBarWidth;

          // Background
          doc.setFillColor(55, 35, 110);
          doc.roundedRect(barX, barY, maxBarWidth, barHeight, 2, 2, "F");

          // Progress bar
          if (value > 60) {
            doc.setFillColor(...COLORS.red);
          } else if (value > 40) {
            doc.setFillColor(...COLORS.orange);
          } else {
            doc.setFillColor(...COLORS.green);
          }

          doc.roundedRect(barX, barY, barWidth, barHeight, 2, 2, "F");

          // TEXT INSIDE CELL (right aligned)
          doc.setFontSize(9);
          doc.setTextColor(...COLORS.lightText);

          doc.text(
            `${value}`,
            data.cell.x + data.cell.width - 2,
            data.cell.y + data.cell.height / 2,
            { align: "right", baseline: "middle" }
          );
        }
        if (data.column.index === opportunityIndex && data.cell.section === "body") {
          const value = String(data.cell.raw || "").toUpperCase();

          const colors = getOpportunityColor(value);

          const paddingX = 4;
          const pillHeight = 6;

          doc.setFontSize(7);
          const textWidth = doc.getTextWidth(value);

          const pillWidth = textWidth + paddingX * 2;

          // Center horizontally + vertically
          const x = data.cell.x + (data.cell.width - pillWidth) / 2;
          const y = data.cell.y + (data.cell.height - pillHeight) / 2;

          // Draw pill background
          doc.setFillColor(...colors.bg);
          doc.setDrawColor(...colors.border);
          doc.roundedRect(x, y, pillWidth, pillHeight, 3, 3, "FD");

          // Draw text
          doc.setTextColor(...colors.text);
          doc.text(
            value.toLowerCase(),
            x + pillWidth / 2,
            y + pillHeight / 2 + 0.5,
            { align: "center", baseline: "middle" }
          );
        }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Position Distribution Table ──
  if (data.positionDistribution && data.positionDistribution.length > 0) {
    sectionTitle("Position Distribution");

    createTable(doc, margin, {
      startY: y,
      head: [["Period", "Keywords", "Top 1-5", "Top 6-10", "Top 11-20", "Top 21-50", "Top 51-100"]],
      body: data.positionDistribution.map((p) => [
        `${p.month}/${p.year}`,
        p.keywordsCount.toLocaleString(),
        p.top1_5.toLocaleString(),
        p.top6_10.toLocaleString(),
        p.top11_20.toLocaleString(),
        p.top21_50.toLocaleString(),
        p.top51_100.toLocaleString(),
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" }
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Position Changes Table ──
  if (data?.positionChanges && data?.positionChanges.length > 0) {
    sectionTitle("Recent  Position Changes");

    createTable(doc, margin, {
      startY: y,
      head: [["Keyword", "Prev Pos", "Current Pos", "Change"]],
      body: data.positionChanges.map((p) => [
        cleanText(p.keyword),
        p.prevPosition ? `#${p.prevPosition}` : "-",
        p.position ? `#${p.position}` : "-",
        p.change ?? 'New',
      ]),
      didParseCell: (data: any) => {
        const { column, cell } = data;

        if (column.index === 3 && data.section === "body") {
          cell.styles.fontStyle = "bold"
          const value = cell.raw;
          if (value > 0) {
            cell.styles.textColor = COLORS.green;
            cell.text = [`+ ${value}`];
          } else if (value < 0) {
            cell.styles.textColor = COLORS.red;
            cell.text = [`- ${Math.abs(value)}`];
          } else {
            cell.styles.textColor = COLORS.orange;
          }
        }
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Content Refresh Opportunities ──
  if (data.contentRefreshOpportunities && data.contentRefreshOpportunities.length > 0) {
    sectionTitle("Content Refresh Opportunities");

    createTable(doc, margin, {
      startY: y,
      head: [["Keyword", "URL", "Volume", "Opportunity", "Reason"]],

      body: data.contentRefreshOpportunities.map((item) => [
        cleanText(item.keyword),
        cleanText(item.url || "-"),
        item.searchVolume?.toLocaleString() || "0",
        item.opportunity?.toUpperCase() || "-",
        item.reason ?? "-"
      ]),
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50, textColor: COLORS.purple },
        2: { cellWidth: 20 },
        3: { cellWidth: 30 },
        4: { cellWidth: 60 },
      },
      didParseCell: (data: any) => {
        if (data.column.index === 3 && data.cell.section === "body") {
          data.cell.text = [''];
        }
      },

      didDrawCell: (data: any) => {
        if (data.column.index === 3 && data.cell.section === "body") {
          const value = String(data.cell.raw || "").toUpperCase();
          const colors = getOpportunityColor(value);

          const paddingX = 4;
          const pillHeight = 6;

          doc.setFontSize(7);
          const textWidth = doc.getTextWidth(value);
          const pillWidth = textWidth + paddingX * 2;

          const x = data.cell.x + (data.cell.width - pillWidth) / 2;
          const y = data.cell.y + (data.cell.height - pillHeight) / 2;

          doc.setFillColor(...colors.bg);
          doc.setDrawColor(...colors.border);
          doc.roundedRect(x, y, pillWidth, pillHeight, 3, 3, "FD");

          doc.setTextColor(...colors.text);
          doc.text(
            value.toLowerCase(),
            x + pillWidth / 2,
            y + pillHeight / 2 + 0.5,
            { align: "center", baseline: "middle" }
          );
        }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Traffic History Table ──
  if (data.trafficHistory && data.trafficHistory.length > 0) {
    sectionTitle("Traffic History");

    createTable(doc, margin, {
      startY: y,
      head: [["Period", "Traffic", "Keywords", "Top 1-5", "Top 6-10", "Top 11-20"]],
      body: data.trafficHistory.map((th) => [
        `${th.month}/${th.year}`,
        th.trafficSum.toLocaleString(),
        th.keywordsCount.toLocaleString(),
        th.top1_5.toLocaleString(),
        th.top6_10.toLocaleString(),
        th.top11_20.toLocaleString(),
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" }
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Worldwide Traffic Overview Table ──
  if (data.worldwideOverview && data.worldwideOverview.length > 0) {
    sectionTitle("Worldwide Traffic Overview");

    createTable(doc, margin, {
      startY: y,
      head: [["Region", "Traffic", "Keywords", "Est. Cst"]],
      body: data.worldwideOverview.map((w) => [
        w.source.toUpperCase(),
        w.traffic.toLocaleString(),
        w.keywords.toLocaleString(),
        `$${w.price.toLocaleString()}`
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" }
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Top Referring Domains Table ──
  if (data.referringDomains && data.referringDomains.length > 0) {
    sectionTitle("Top Referring Domains");

    createTable(doc, margin, {
      startY: y,
      head: [["Domain", "Backlinks", "Domain Authority"]],
      body: data.referringDomains.map((r) => [
        r.domain,
        r.backlinks.toLocaleString(),
        r.domainAuthority.toLocaleString()
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Anchor Text Distribution Table ──
  if (data.anchorText && data.anchorText.length > 0) {
    sectionTitle("Anchor Text Distribution");

    createTable(doc, margin, {
      startY: y,
      head: [["Anchor Text", "Backlinks", "Referring Domains"]],
      body: data.anchorText.map((a) => [
        a.anchor,
        a.backlinksCount.toLocaleString(),
        a.referringDomainsCount.toLocaleString()
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Question Keywords ──
  if (data.questionKeywords && data.questionKeywords.length > 0) {
    sectionTitle("Question Keywords");

    data.questionKeywords.forEach((k) => {
      checkPageBreak(16);
      doc.setFillColor(...COLORS.bgCard);
      doc.roundedRect(margin, y, contentWidth, 12, 2, 2, "F");

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.lightText);
      doc.text(cleanText(k.keyword), margin + 12, y + 7);

      doc.text(cleanText(`Vol: ${k.volume ?? "-"}`), margin + contentWidth - 40, y + 7);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.green);
      doc.text(cleanText(`Diff: ${k.difficulty}`), margin + contentWidth - 15, y + 7);

      y += 15;
    });
    y += 5;
  }

  // ── SERP Feature Keywords ──
  if (data.serpFeatures && data.serpFeatures.length > 0) {
    sectionTitle("SERP Feature Keywords");

    const SERP_COL_WIDTH = 70;

    createTable(doc, margin, {
      startY: y,
      head: [["Keyword", "Position", "Volume", "Traffic", "SERP Features"]],
      body: data.serpFeatures.map((kw) => [
        cleanText(kw.keyword),
        kw.position ? `#${kw.position}` : "-",
        Number(kw.volume ?? 0).toLocaleString(),
        kw.traffic?.toLocaleString() ?? "0",
        kw.serpFeatures ?? []
      ]),
      columnStyles: {
        0: { cellWidth: 30 },
        3: { textColor: COLORS.orange, fontStyle: "bold" },
        4: { cellWidth: SERP_COL_WIDTH }
      },
      didParseCell: (data: any) => {
        if (data.column.index === 4 && data.cell.section === "body") {
          const features = data.cell.raw as string[];
          if (!Array.isArray(features)) return;

          data.cell.text = [''];

          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);

          const gapX = 2;
          const gapY = 2;
          const paddingX = 5;
          const pillHeight = 5;

          const maxWidth = SERP_COL_WIDTH - 5;

          let x = 0;
          let y = 0;

          features.forEach((feature: string) => {
            const text = cleanText(feature);
            const textWidth = doc.getTextWidth(text);
            const pillWidth = textWidth + paddingX;

            if (x + pillWidth > maxWidth) {
              x = 0;
              y += pillHeight + gapY;
            }

            x += pillWidth + gapX;
          });

          const totalHeight = y + pillHeight;

          // Add enough padding
          data.row.height = Math.max(data.row.height, totalHeight + 6);
        }
      },

      didDrawCell: (data: any) => {
        if (data.column.index === 4 && data.cell.section === "body") {
          const features = data.cell.raw as string[];
          if (!Array.isArray(features)) return;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);

          const gapX = 2;
          const gapY = 2;
          const paddingX = 5;
          const pillHeight = 5;

          const startX = data.cell.x + 3;
          let x = startX;
          let y = data.cell.y + 3;

          const maxWidth = SERP_COL_WIDTH - 5;

          features.forEach((feature: string) => {
            const text = cleanText(feature);
            const textWidth = doc.getTextWidth(text);
            const pillWidth = textWidth + paddingX;

            if (x + pillWidth > startX + maxWidth) {
              x = startX;
              y += pillHeight + gapY;
            }

            doc.setFillColor(70, 50, 180); // soft purple
            doc.setDrawColor(90, 70, 220);

            doc.roundedRect(x, y, pillWidth, pillHeight, 1.5, 1.5, "F");

            doc.setTextColor(200, 190, 255);
            doc.text(text, x + 3, y + 3.5);

            x += pillWidth + gapX;
          });
        }
      }

    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Competitor Comparison ──
  if (data.competitors && data.competitors.length > 0) {
    sectionTitle("Competitor Comparison");

    createTable(doc, margin, {
      startY: y,
      head: [["Domain", "Authority", "Est. Traffic", "Common Keywords", "Overlap %"]],
      body: data.competitors.map((comp) => [
        comp.domain,
        `${comp.authorityScore}/100`,
        comp.traffic.toLocaleString(),
        comp.commonKeywords.toLocaleString(),
        comp.overlap
      ]),
      columnStyles: {
        2: { textColor: COLORS.orange, fontStyle: "bold" }
      },
      didParseCell: (data: any) => {
        if (data.column.index === 4 && data.cell.section === "body") {
          data.cell.text = [''];
        }
      },
      didDrawCell: (data: any) => {
        if (data.column.index === 4 && data.cell.section === "body") {
          const raw = data.cell.raw;

          const value = typeof raw === "string"
            ? parseFloat(raw.replace("%", ""))
            : Number(raw || 0);

          const padding = 2;
          const barHeight = 3;

          const barX = data.cell.x + padding;
          const barY = data.cell.y + (data.cell.height / 2) - (barHeight / 2);

          const maxBarWidth = data.cell.width - 15;
          const barWidth = (value / 100) * maxBarWidth;

          // Background bar (track)
          doc.setFillColor(60, 45, 120);
          doc.roundedRect(barX, barY, maxBarWidth, barHeight, 1, 1, "F");

          // Progress bar
          doc.setFillColor(140, 120, 255);
          doc.roundedRect(barX, barY, barWidth, barHeight, 0.5, 0.5, "F");

          // Percentage text
          doc.setFontSize(8);
          doc.setTextColor(200, 190, 255);

          doc.text(
            `${value.toFixed(2)}%`,
            data.cell.x + data.cell.width - 2,
            data.cell.y + data.cell.height / 2,
            {
              align: "right",
              baseline: "middle"
            }
          );
        }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 10;

    sectionTitle("Estimated Market Share");

    createTable(doc, margin, {
      startY: y,
      head: [["Domain", "Est. Traffic", "Market Share %"]],
      body: data.competitors.map((comp) => [
        comp.domain,
        comp.traffic.toLocaleString(),
       `${comp.share}%`
      ]),
      columnStyles: {
        1: { textColor: COLORS.orange, fontStyle: "bold" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Top Pages by Traffic Table ──
  if (data.topPages && data.topPages.length > 0) {
    sectionTitle("Top Domain Pages");

    createTable(doc, margin, {
      startY: y,
      head: [["URL", "Keywords", "Traffic"]],
      body: data.topPages.map((t) => [
        t.url,
        t.keywordsCount.toLocaleString(),
        t.trafficSum.toLocaleString()
      ]),
      columnStyles: {
        2: { textColor: COLORS.orange, fontStyle: "bold", cellWidth: 20 },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── New & Lost Links Table ──
  // ── New Links ──
  if (data.newLinks && data.newLinks.length > 0) {
    sectionTitle("New Links");

    createTable(doc, margin, {
      startY: y,
      head: [["From URL", "To URL", "Anchor", "First Seen"]],
      body: data.newLinks.map((link) => [
        cleanText(link.urlFrom),
        cleanText(link.urlTo),
        cleanText(link.anchor || "-"),
        cleanText(link.firstSeen),
      ]),
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 55 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25, textColor: COLORS.green, fontStyle: "bold" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Lost Links ──
  if (data.lostLinks && data.lostLinks.length > 0) {
    sectionTitle("Lost Links");

    createTable(doc, margin, {
      startY: y,
      head: [["From URL", "To URL", "Anchor", "First Seen"]],
      body: data.lostLinks.map((link) => [
        cleanText(link.urlFrom),
        cleanText(link.urlTo),
        cleanText(link.anchor || "-"),
        cleanText(link.firstSeen),
      ]),
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 55 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25, textColor: COLORS.red, fontStyle: "bold" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Domain Overview Grid ──
  if (data?.summaryMetrics?.metrics?.length > 0) {
    sectionTitle(data?.summaryMetrics?.title);

    const boxWidth = (contentWidth - 10) / 3;
    const boxHeight = 22;

    data?.summaryMetrics?.metrics?.forEach((item, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);

      const x = margin + col * (boxWidth + 5);
      const boxY = y + row * (boxHeight + 6);

      doc.setFillColor(...COLORS.bgCard);
      doc.roundedRect(x, boxY, boxWidth, boxHeight, 3, 3, "F");

      doc.setFontSize(7);
      doc.setTextColor(160, 155, 200);
      doc.text(item.label.toUpperCase(), x + 4, boxY + 8);

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(String(item.value), x + 4, boxY + 16);
    });

    y += Math.ceil(data.summaryMetrics?.metrics.length / 3) * (boxHeight + 6) + 5;
  }

  // ── Detailed Analysis ──
  if (data.analysis && data.analysis.length > 50) {
    newPage();

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.white);
    doc.text("Detailed Analysis", margin, y);
    y += 3;
    doc.setDrawColor(...COLORS.purple);
    doc.setLineWidth(0.5);
    doc.line(margin, y, margin + 60, y);
    y += 10;

    const cleaned = cleanText(data.analysis);

    // Split into paragraphs
    const paragraphs = cleaned.split(/\n+/).filter((p) => p.trim());

    for (const para of paragraphs) {
      const trimmed = para.trim();
      if (!trimmed) continue;

      // Detect section headers (short lines that look like titles)
      const isHeader = trimmed.length < 60 && !trimmed.includes(".") && (
        trimmed.endsWith(":") || trimmed === trimmed.replace(/[a-z]/g, "") || /^[A-Z]/.test(trimmed)
      );

      if (isHeader) {
        checkPageBreak(12);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLORS.white);
        doc.text(trimmed, margin, y);
        y += 7;
      } else {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...COLORS.lightText);

        const lines = doc.splitTextToSize(trimmed, contentWidth);
        for (const line of lines) {
          checkPageBreak(5);
          doc.text(line, margin, y);
          y += 4.5;
        }
        y += 2;
      }
    }
  }

  // ── Add footers to all pages ──
  const totalPages = pageNum;
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    doc.text(
      `OG01 Ruby AI Suite  |  ${data.generatedAt}  |  Page ${p} of ${totalPages}`,
      margin,
      pageHeight - 10
    );
  }

  doc.save(`OG01_Search_Intelligence_Report_${data.domain.replace(/\./g, "_")}.pdf`);
}
